/**
 * OperatingModesRunner — the in-browser isolation runner for an equipment's
 * declared FSM: drive the raw signals (toggle a coil, ramp an analog past its
 * level, fire a count / operator event) and watch `equipment_state` step through
 * the modes on a live timeline. It runs the platform-faithful evaluator from
 * `operating-modes-run.ts`, so it previews exactly what the server's predicate
 * runtime will derive once the run goes over the wire — the sim drives signals,
 * the state is DERIVED, never asserted.
 *
 * This is a SIM-tool surface (authoring / emulation), not part of the customer
 * portal's operational view; the caller mounts it in the agent workspace.
 */

import { useEffect, useMemo, useState } from "react";
import { Gauge, Pause, Play, RotateCcw, StepForward, Zap } from "lucide-react";

import type { EntityDef } from "@foundry/foundry-plant";
import {
  type RunState,
  collectControls,
  createRun,
  fireEvent,
  setAnalog,
  setCoil,
  stepRun,
} from "@/lib/plant/operating-modes-run";
import { MODE_HUES } from "@/lib/plant/operating-modes-viz";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";

/** The live time span shown, so the "now" marker sits near the right edge as it grows. */
function spanFor(t: number): number {
  return Math.max(8, t * 1.15);
}

function hueForMode(modes: string[], mode: string): number {
  const i = modes.indexOf(mode);
  return MODE_HUES[(i < 0 ? 0 : i) % MODE_HUES.length];
}

export interface OperatingModesRunnerProps {
  /** Must declare operating_modes; the caller gates + keys on entity.id. */
  entity: EntityDef;
}

/** Interactive isolation runner: drive signals, watch the FSM derive state on a timeline. */
export function OperatingModesRunner({ entity }: OperatingModesRunnerProps) {
  const om = entity.operating_modes;
  const modes = useMemo(() => om?.modes ?? [], [om]);
  const controls = useMemo(() => (om ? collectControls(om) : { coils: [], analogs: [], events: [] }), [om]);

  const [run, setRun] = useState<RunState>(() =>
    om ? createRun(om, controls) : ({} as RunState),
  );
  const [running, setRunning] = useState(false);
  const [speed, setSpeed] = useState(4);

  // The clock: while running, advance the evaluator every 100ms at the chosen speed.
  useEffect(() => {
    if (!running || !om) return;
    const id = setInterval(() => setRun((s) => stepRun(s, om, 0.1 * speed)), 100);
    return () => clearInterval(id);
  }, [running, speed, om]);

  if (!om || modes.length === 0) return null;

  const span = spanFor(run.t);
  const nothingDrivable =
    controls.coils.length === 0 && controls.analogs.length === 0 && controls.events.length === 0;

  const reset = () => {
    setRunning(false);
    setRun(createRun(om, controls));
  };
  const stepOnce = () => setRun((s) => stepRun(s, om, 0.2 * Math.max(1, speed)));
  const fire = (onEvent: string) => setRun((s) => stepRun(fireEvent(s, onEvent), om, 0.1 * speed));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2 pb-2">
        <CardTitle className="flex flex-1 items-center gap-2 text-sm">
          <Gauge className="size-4" /> Isolation runner
          <Badge variant="secondary" className="px-1.5 py-0 text-[11px]">
            preview
          </Badge>
        </CardTitle>
        <span className="font-mono text-xs text-on-surface-muted">t={run.t.toFixed(1)}s</span>
      </CardHeader>

      <CardContent className="flex flex-col gap-3">
        {/* Transport */}
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant={running ? "secondary" : "primary"} onClick={() => setRunning((r) => !r)}>
            {running ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
            {running ? "Pause" : "Run"}
          </Button>
          <Button size="sm" variant="outline" onClick={stepOnce} disabled={running}>
            <StepForward className="size-3.5" /> Step
          </Button>
          <Button size="sm" variant="ghost" onClick={reset}>
            <RotateCcw className="size-3.5" /> Reset
          </Button>
          <label className="ml-auto flex items-center gap-1.5 text-[11px] text-on-surface-muted">
            speed
            <input
              type="range"
              min={1}
              max={30}
              value={speed}
              aria-label="Run speed"
              className="w-20 align-middle"
              onChange={(e) => setSpeed(Number(e.target.value))}
            />
            <span className="w-7 font-mono">{speed}×</span>
          </label>
        </div>

        {/* Current state + live timeline */}
        <div className="flex items-center gap-2 text-xs">
          <span className="text-on-surface-muted">state</span>
          <span
            className="rounded-md border border-l-4 px-1.5 py-0.5 font-mono font-semibold text-on-surface"
            style={{
              backgroundColor: `hsl(${hueForMode(modes, run.mode)} 48% 50% / 0.14)`,
              borderColor: `hsl(${hueForMode(modes, run.mode)} 45% 50% / 0.6)`,
            }}
          >
            {run.mode}
          </span>
        </div>
        <div className="relative h-14 w-full overflow-hidden rounded-md border border-border bg-surface-muted/30">
          {run.segments.map((seg, i) => {
            const end = seg.t1 ?? run.t;
            const left = (seg.t0 / span) * 100;
            const width = Math.max(0, ((end - seg.t0) / span) * 100);
            const hue = hueForMode(modes, seg.mode);
            return (
              <div
                key={`${seg.mode}-${i}`}
                className="absolute inset-y-0 flex items-center overflow-hidden border-l-2 pl-1"
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  backgroundColor: `hsl(${hue} 48% 50% / 0.18)`,
                  borderColor: `hsl(${hue} 45% 50% / 0.65)`,
                }}
                title={`${seg.mode} @ ${seg.t0.toFixed(1)}s`}
              >
                {width > 9 && (
                  <span className="truncate font-mono text-[10px] text-on-surface">{seg.mode}</span>
                )}
              </div>
            );
          })}
          <div
            className="absolute inset-y-0 w-0.5 bg-brand"
            style={{ left: `${(run.t / span) * 100}%` }}
            aria-hidden
          />
        </div>

        {/* Signal controls */}
        {nothingDrivable ? (
          <p className="text-[11px] text-on-surface-muted">
            This model has no streaming-driven transitions to drive by hand (all human / time-based).
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <span className="text-xs font-medium text-on-surface-muted">Drive the signals</span>

            {controls.analogs.map((a) => {
              const value = run.analogs[a.key] ?? a.min;
              const step = (a.max - a.min) / 200 || 1;
              return (
                <div key={a.key} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="min-w-56 font-mono text-on-surface">
                    {a.key}
                    {a.unit ? <span className="text-on-surface-muted"> {a.unit}</span> : null}
                  </span>
                  <input
                    type="range"
                    min={a.min}
                    max={a.max}
                    step={step}
                    value={value}
                    aria-label={`Drive ${a.key}`}
                    className="w-40"
                    onChange={(e) => setRun((s) => setAnalog(s, a.key, Number(e.target.value)))}
                  />
                  <span className="w-16 font-mono tabular-nums text-on-surface-muted">
                    {value.toFixed(1)}
                  </span>
                </div>
              );
            })}

            {controls.coils.map((c) => {
              const on = (run.coils[c.key] ?? 0) >= 0.5;
              return (
                <div key={c.key} className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="min-w-56 font-mono text-on-surface">{c.key}</span>
                  <div className="flex overflow-hidden rounded-md border border-border">
                    {[0, 1].map((v) => (
                      <button
                        key={v}
                        type="button"
                        aria-pressed={on === (v === 1)}
                        className={
                          on === (v === 1)
                            ? "bg-brand/15 px-2 py-0.5 font-mono text-[11px] text-on-surface"
                            : "px-2 py-0.5 font-mono text-[11px] text-on-surface-muted"
                        }
                        onClick={() => setRun((s) => setCoil(s, c.key, v))}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}

            {controls.events.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[11px] text-on-surface-muted">fire</span>
                {controls.events.map((e) => (
                  <Button
                    key={e.on_event}
                    size="sm"
                    variant="outline"
                    className="h-6 px-1.5 font-mono text-[11px]"
                    onClick={() => fire(e.on_event)}
                  >
                    <Zap className="size-3" /> {e.on_event}
                  </Button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Transition log */}
        {run.log.length > 0 && (
          <div className="flex flex-col gap-0.5 rounded-md border border-border bg-surface-muted/30 px-2.5 py-1.5">
            <span className="text-[11px] font-medium text-on-surface-muted">Transitions fired</span>
            {run.log.slice(0, 8).map((l, i) => (
              <div key={i} className="flex flex-wrap items-center gap-1.5 text-[11px]">
                <span className="w-12 font-mono tabular-nums text-on-surface-muted">
                  {l.t.toFixed(1)}s
                </span>
                <span className="font-mono text-on-surface-muted">{l.from}</span>
                <span aria-hidden className="text-on-surface-muted">
                  {"->"}
                </span>
                <span className="font-mono font-semibold text-on-surface">{l.to}</span>
                <span className="text-on-surface-muted">on</span>
                <Badge variant="outline" className="px-1.5 py-0 font-mono text-[11px]">
                  {l.on_event}
                </Badge>
              </div>
            ))}
          </div>
        )}

        <p className="text-[10px] text-on-surface-muted">
          In-browser preview of the platform&apos;s predicate runtime. The real run derives state
          server-side over the wire; the sim only drives the signals.
        </p>
      </CardContent>
    </Card>
  );
}
